// Table Assignment Utilities
//
// This module provides functionality to automatically assign tables to tournament matches
// based on tournament settings and match availability.

import {
  getEstimatedMatchDuration,
  calculateMatchEndTime,
  getSlowMatches
} from './matchTimingUtils';

/**
 * Generate available table numbers based on tournament settings
 * @param {Object} settings - Table configuration settings
 * @param {string} settings.customTableNumbers - Comma-separated custom table numbers
 * @returns {string[]} Array of available table numbers as strings
 */
export const getAvailableTables = (settings) => {
  const { customTableNumbers } = settings;

  // Always use custom table numbers (comma-separated list)
  return customTableNumbers
    .split(',')
    .map(t => t.trim())
    .filter(Boolean);
};

/**
 * Check if a match is eligible for table assignment
 * @param {Object} match - Tournament match object
 * @returns {boolean} True if match needs and can receive a table assignment
 */
export const isMatchEligibleForTable = (match) => {
  const hasValidPlayers = match.p1?.name && match.p2?.name &&
    !match.p1.name.startsWith('Winner of') && !match.p1.name.startsWith('Loser of') &&
    !match.p2.name.startsWith('Winner of') && !match.p2.name.startsWith('Loser of') &&
    match.p1.name !== 'BYE' && match.p2.name !== 'BYE';

  return hasValidPlayers && !match.finished;
};

/**
 * Get matches that need table assignments, sorted by priority
 * @param {Object[]} bracket - Array of tournament matches
 * @returns {Object[]} Sorted array of matches needing table assignments
 */
export const getMatchesNeedingTables = (bracket) => {
  const eligibleMatches = bracket.filter(isMatchEligibleForTable);

  // Sort matches by priority: round first, then match number
  return eligibleMatches.sort((a, b) => {
    if (a.round !== b.round) return a.round - b.round;
    return a.matchNum - b.matchNum;
  });
};

/**
 * Get currently used tables from eligible (ready to play) matches only
 * Finished matches keep their table for display but don't count as "using" the table
 * @param {Object[]} bracket - Array of tournament matches
 * @returns {Set} Set of currently assigned table numbers from active matches
 */
export const getCurrentlyUsedTables = (bracket) => {
  const usedTables = new Set();
  bracket.forEach(match => {
    // Only count tables from matches that are eligible AND not finished
    if (match.table && match.table !== "" && isMatchEligibleForTable(match) && !match.finished) {
      usedTables.add(match.table);
    }
  });
  return usedTables;
};

/**
 * Get available tables that are not currently in use
 * @param {Object} tableSettings - Table configuration settings
 * @param {Object[]} bracket - Array of tournament matches
 * @returns {string[]} Array of unused table numbers
 */
export const getUnusedTables = (tableSettings, bracket) => {
  const allTables = getAvailableTables(tableSettings);
  const usedTables = getCurrentlyUsedTables(bracket);

  return allTables.filter(table => !usedTables.has(table));
};

/**
 * Initial table assignment for new tournaments
 * @param {Object[]} bracket - Array of tournament matches
 * @param {Object} tableSettings - Table configuration settings
 * @returns {Object[]} Updated bracket with table assignments
 */
export const assignTablesToMatches = (bracket, tableSettings) => {
  const allAvailableTables = getAvailableTables(tableSettings);

  if (allAvailableTables.length === 0) {
    console.warn('No tables available for assignment');
    return bracket;
  }

  // Only assign to matches that don't have tables and haven't started
  const matchesNeedingTables = bracket.filter(match => {
    return isMatchEligibleForTable(match) && (!match.table || match.table === "") && !match.started;
  });

  // Sort by priority
  matchesNeedingTables.sort((a, b) => {
    if (a.round !== b.round) return a.round - b.round;
    return a.matchNum - b.matchNum;
  });

  // Get currently used tables to avoid duplicates
  const usedTables = getCurrentlyUsedTables(bracket);
  const unusedTables = allAvailableTables.filter(table => !usedTables.has(table));

  // Create assignments
  const tableAssignments = new Map();
  let tableIndex = 0;

  matchesNeedingTables.forEach(match => {
    if (tableIndex < unusedTables.length) {
      tableAssignments.set(match.id, unusedTables[tableIndex]);
      tableIndex++;
    }
  });

  // Update bracket with new table assignments
  return bracket.map(match => {
    if (tableAssignments.has(match.id)) {
      return { ...match, table: tableAssignments.get(match.id) };
    }
    return match;
  });
};

/**
 * Shuffle table assignments randomly while maintaining match priority by round and match number
 * @param {Object[]} bracket - Array of tournament matches
 * @param {Object} tableSettings - Table configuration settings
 * @returns {Object[]} Updated bracket with shuffled table assignments
 */
export const shuffleTableAssignments = (bracket, tableSettings) => {
  const allAvailableTables = getAvailableTables(tableSettings);

  if (allAvailableTables.length === 0) {
    console.warn('No tables available for assignment');
    return bracket;
  }

  // Get ALL eligible matches that haven't started and clear their assignments
  const eligibleMatches = bracket.filter(match => isMatchEligibleForTable(match) && !match.started);

  // Clear all existing table assignments for eligible matches that haven't started
  let updatedBracket = bracket.map(match => {
    if (isMatchEligibleForTable(match) && !match.started) {
      return { ...match, table: "" };
    }
    return match;
  });

  // Sort matches by priority: round first, then match number (DON'T shuffle the matches)
  const prioritizedMatches = [...eligibleMatches].sort((a, b) => {
    if (a.round !== b.round) return a.round - b.round;
    return a.matchNum - b.matchNum;
  });

  // Shuffle the table numbers instead of the matches
  const shuffledTables = [...allAvailableTables].sort(() => Math.random() - 0.5);

  // Assign shuffled tables to prioritized matches
  const tableAssignments = new Map();
  let tableIndex = 0;

  prioritizedMatches.forEach(match => {
    if (tableIndex < shuffledTables.length) {
      tableAssignments.set(match.id, shuffledTables[tableIndex]);
      tableIndex++;
    }
  });

  // Update bracket with shuffled assignments
  return updatedBracket.map(match => {
    if (tableAssignments.has(match.id)) {
      return { ...match, table: tableAssignments.get(match.id) };
    }
    return match;
  });
};

/**
 * Assign a freed table to the next available match
 * @param {Object[]} bracket - Array of tournament matches
 * @param {string} freedTable - Table number that became available
 * @returns {Object[]} Updated bracket with the freed table assigned
 */
export const assignFreedTable = (bracket, freedTable) => {
  // Find the next eligible match without a table (priority order)
  const matchesNeedingTables = bracket
    .filter(match => isMatchEligibleForTable(match) && (!match.table || match.table === ""))
    .sort((a, b) => {
      if (a.round !== b.round) return a.round - b.round;
      return a.matchNum - b.matchNum;
    });

  if (matchesNeedingTables.length > 0) {
    const nextMatch = matchesNeedingTables[0];
    return bracket.map(match =>
      match.id === nextMatch.id ? { ...match, table: freedTable } : match
    );
  }

  return bracket; // No matches need tables
};

/**
 * Reassign tables after a manual change to prevent duplicates
 * @param {Object[]} bracket - Array of tournament matches
 * @param {Object} tableSettings - Table configuration settings
 * @param {string} changedMatchId - ID of the match that was manually changed
 * @param {string} newTableNumber - New table number assigned to the changed match
 * @returns {Object[]} Updated bracket with reassigned tables
 */
export const reassignTablesAfterManualChange = (bracket, tableSettings, changedMatchId, newTableNumber) => {
  const allAvailableTables = getAvailableTables(tableSettings);

  // Validate the new table number (allow empty string to clear assignment)
  if (newTableNumber !== "" && !allAvailableTables.includes(newTableNumber)) {
    console.warn(`Table number "${newTableNumber}" is not available. Available tables: ${allAvailableTables.join(', ')}`);
    return bracket; // Don't allow invalid table numbers
  }

  // Get the original table number from the match being changed
  const originalMatch = bracket.find(match => match.id === changedMatchId);
  const originalTableNumber = originalMatch?.table || "";

  // Step 1: Find any ACTIVE match that's currently using the new table number
  // (finished matches keep their table for display but don't count as conflicts)
  const conflictingMatch = bracket.find(match =>
    match.id !== changedMatchId &&
    match.table === newTableNumber &&
    isMatchEligibleForTable(match) &&
    !match.finished
  );

  // Step 2: Update all matches
  let updatedBracket = bracket.map(match => {
    if (match.id === changedMatchId) {
      // Assign the new table to the target match
      return { ...match, table: newTableNumber };
    } else if (conflictingMatch && match.id === conflictingMatch.id) {
      // Remove table from the conflicting match
      return { ...match, table: "" };
    }
    return match;
  });

  // Step 3: If we displaced a match AND the user is assigning a table (not clearing),
  // try to give the displaced match a table
  if (conflictingMatch && newTableNumber !== "") {
    if (originalTableNumber && originalTableNumber !== "" && originalTableNumber !== newTableNumber) {
      // The original table is now free, assign it to the displaced match
      updatedBracket = updatedBracket.map(match =>
        match.id === conflictingMatch.id ? { ...match, table: originalTableNumber } : match
      );
    } else {
      // No table to swap back, find any available table for the displaced match
      const usedTables = getCurrentlyUsedTables(updatedBracket);
      const availableTable = allAvailableTables.find(table => !usedTables.has(table));

      if (availableTable) {
        updatedBracket = updatedBracket.map(match =>
          match.id === conflictingMatch.id ? { ...match, table: availableTable } : match
        );
      }
      // If no available table, the displaced match stays without a table
    }
  }

  return updatedBracket;
};

/**
 * Clear all table assignments from matches
 * @param {Object[]} bracket - Array of tournament matches
 * @returns {Object[]} Updated bracket with cleared table assignments
 */
export const clearAllTableAssignments = (bracket) => {
  return bracket.map(match => ({
    ...match,
    table: ""
  }));
};

/**
 * Validate table assignments and find issues
 * @param {Object[]} bracket - Array of tournament matches
 * @param {Object} tableSettings - Table configuration settings
 * @returns {Object} Validation results with issues found
 */
export const validateTableAssignments = (bracket, tableSettings) => {
  const availableTables = getAvailableTables(tableSettings);
  const eligibleMatches = bracket.filter(isMatchEligibleForTable);
  const issues = [];

  // Check for duplicate table assignments
  const tableUsage = {};
  eligibleMatches.forEach(match => {
    if (match.table && match.table !== "") {
      if (!tableUsage[match.table]) {
        tableUsage[match.table] = [];
      }
      tableUsage[match.table].push(match);
    }
  });

  // Find duplicates
  Object.entries(tableUsage).forEach(([table, matches]) => {
    if (matches.length > 1) {
      issues.push({
        type: 'duplicate',
        table,
        matches: matches.map(m => ({ id: m.id, matchNum: m.matchNum }))
      });
    }
  });

  // Check for invalid table numbers
  eligibleMatches.forEach(match => {
    if (match.table && match.table !== "" && !availableTables.includes(match.table)) {
      issues.push({
        type: 'invalid',
        table: match.table,
        match: { id: match.id, matchNum: match.matchNum }
      });
    }
  });

  return {
    isValid: issues.length === 0,
    issues
  };
};

/**
 * Get table assignment statistics
 * @param {Object[]} bracket - Array of tournament matches
 * @param {Object} tableSettings - Table configuration settings
 * @returns {Object} Statistics about table usage
 */
export const getTableAssignmentStats = (bracket, tableSettings) => {
  const availableTables = getAvailableTables(tableSettings);
  const eligibleMatches = bracket.filter(isMatchEligibleForTable);
  const assignedMatches = eligibleMatches.filter(match => match.table && match.table !== "");

  // Count matches per table
  const tableUsage = {};
  availableTables.forEach(table => {
    tableUsage[table] = assignedMatches.filter(match => match.table === table).length;
  });

  const validation = validateTableAssignments(bracket, tableSettings);

  return {
    totalTables: availableTables.length,
    eligibleMatches: eligibleMatches.length,
    assignedMatches: assignedMatches.length,
    unassignedMatches: eligibleMatches.length - assignedMatches.length,
    tableUsage,
    validation
  };
};

// Re-export timing utilities for convenience
export {
  getEstimatedMatchDuration,
  calculateMatchEndTime,
  getSlowMatches
};